import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, LineChart, Line } from 'recharts';
import { ChevronLeft, Star, ArrowUp, ArrowDown, Trophy } from 'lucide-react';
import {
  getSsgPeriods, getSsgPlayerSummary, getSsgLeaderboard, getSsgProgression,
  getSsgHeadToHead, getSsgHallOfFame,
} from '../../api';
import { SSG_COMPETITION_NAME, SSG_BADGES, ssgResultMeta } from '../../ssgConstants';

const CARD = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: '14px 16px', marginBottom: 12 };
const CARD_TITLE = { fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 };
const RANK_TINT = { 1: '#f59e0b', 2: '#94a3b8', 3: '#b45309' };

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

function Avatar({ name, photo, size = 44 }) {
  if (photo) return <img src={photo} alt="" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />;
  const initials = (name || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0, background: 'linear-gradient(135deg,#2563eb,#3b82f6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: size * 0.36, color: '#fff',
    }}>{initials}</div>
  );
}

function FormPills({ form }) {
  if (!form?.length) return <span style={{ color: 'var(--text-dimmed)', fontSize: 12 }}>No sessions played yet.</span>;
  return (
    <div style={{ display: 'flex', gap: 5 }}>
      {form.map((r, i) => {
        const meta = ssgResultMeta({ W: 3, D: 1, L: 0 }[r]);
        return (
          <span key={i} style={{
            width: 26, height: 26, borderRadius: 6, fontSize: 12, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: meta.color + '33', color: meta.color,
          }}>{r}</span>
        );
      })}
    </div>
  );
}

export default function PlayerSsg({ playerRecord }) {
  const navigate = useNavigate();
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState(null);
  const [summary, setSummary] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [progression, setProgression] = useState([]);
  const [hallOfFame, setHallOfFame] = useState(null);
  const [duelRivalId, setDuelRivalId] = useState(null);
  const [headToHead, setHeadToHead] = useState(null);
  const [loading, setLoading] = useState(true);

  const playerId = playerRecord?.id;

  useEffect(() => {
    getSsgPeriods().then(rows => {
      setPeriods(rows);
      // Prefer the active period; once it closes there's a gap before staff manually activate
      // the next one (nothing in this app does that automatically) — fall back to the most
      // recently closed period rather than periods[0], so a player still sees the champion
      // announcement instead of an unrelated older period.
      const active = rows.find(p => p.status === 'active');
      const mostRecentlyClosed = [...rows].filter(p => p.status === 'closed').sort((a, b) => b.period_number - a.period_number)[0];
      const fallback = active || mostRecentlyClosed || rows[0];
      setPeriodId(fallback?.id ?? null);
    });
  }, []);

  useEffect(() => {
    if (!playerId || !periodId) return;
    setLoading(true);
    Promise.all([
      getSsgPlayerSummary(playerId, periodId),
      getSsgLeaderboard(periodId, 'period'),
      getSsgProgression(periodId),
    ]).then(([sum, lb, prog]) => {
      setSummary(sum);
      setLeaderboard(lb || []);
      setProgression(prog || []);
      setDuelRivalId(sum?.nearest_rival?.player_id ?? null);
    }).finally(() => setLoading(false));
  }, [playerId, periodId]);

  const activePeriod = periods.find(p => p.id === periodId);

  useEffect(() => {
    if (activePeriod?.season) getSsgHallOfFame(activePeriod.season).then(setHallOfFame);
  }, [activePeriod?.season]);

  const loadHeadToHead = useCallback((rivalId) => {
    if (!playerId || !rivalId) return;
    getSsgHeadToHead(playerId, rivalId, periodId).then(setHeadToHead);
  }, [playerId, periodId]);

  useEffect(() => { if (duelRivalId) loadHeadToHead(duelRivalId); }, [duelRivalId, loadHeadToHead]);

  const myLeaderboardRow = useMemo(() => leaderboard.find(r => r.player_id === playerId), [leaderboard, playerId]);
  const isClosedPeriod = activePeriod?.status === 'closed';
  const periodChampions = useMemo(() => isClosedPeriod ? leaderboard.filter(r => r.rank === 1) : [], [isClosedPeriod, leaderboard]);
  const iAmChampion = periodChampions.some(c => c.player_id === playerId);
  const rival = useMemo(() => leaderboard.find(r => r.player_id === duelRivalId), [leaderboard, duelRivalId]);

  const mySeries = useMemo(() => progression.find(p => p.player_id === playerId)?.series || [], [progression, playerId]);
  const sparklineData = useMemo(() => mySeries.map(pt => ({ cumulative: pt.cumulative })), [mySeries]);

  const bestSession = useMemo(() => {
    const history = summary?.session_history || [];
    if (!history.length) return null;
    return history.reduce((best, s) => {
      const total = (s.base_points || 0) + (s.bonus_points || 0);
      const bestTotal = best ? (best.base_points || 0) + (best.bonus_points || 0) : -1;
      return total > bestTotal ? s : best;
    }, null);
  }, [summary]);

  const daysRemaining = useMemo(() => {
    if (!activePeriod?.end_date) return null;
    const diff = Math.ceil((new Date(activePeriod.end_date) - new Date()) / 86400000);
    return diff >= 0 ? diff : null;
  }, [activePeriod]);

  const earnedBadgeKeys = useMemo(() => new Set((summary?.badges || []).map(b => b.key)), [summary]);

  if (loading || !summary) {
    return (
      <div style={{ padding: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
        {loading ? 'Loading…' : 'No standings yet.'}
      </div>
    );
  }

  const rankTint = RANK_TINT[summary.rank];

  return (
    <div style={{ padding: '14px 14px 24px' }}>
      <button onClick={() => navigate('/player/me')} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 13, marginBottom: 16, padding: 0, fontFamily: 'inherit' }}>
        <ChevronLeft size={16} /> Back
      </button>

      <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Trophy size={18} color="#f59e0b" /> {SSG_COMPETITION_NAME}
      </div>

      {/* Champion announcement — shown to every player, not just the winner(s) */}
      {isClosedPeriod && periodChampions.length > 0 && (
        <div style={{ ...CARD, border: '2px solid #f59e0b', background: 'linear-gradient(135deg, #f59e0b22, var(--bg-card))', textAlign: 'center' }}>
          <div style={{ fontSize: 32 }}>🏆</div>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 4 }}>
            Period Champion — {activePeriod.name}
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, marginTop: 4 }}>
            {iAmChampion ? 'You won! 🎉' : periodChampions.map(c => c.name).join(' & ')}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            {periodChampions[0].total_points} pts{activePeriod.prize ? ` · 🎁 ${activePeriod.prize}` : ''}
          </div>
        </div>
      )}

      {/* 1. Hero */}
      <div style={{
        ...CARD, background: rankTint ? `linear-gradient(135deg, ${rankTint}22, var(--bg-card))` : CARD.background,
        border: rankTint ? `1px solid ${rankTint}66` : CARD.border, textAlign: 'center', padding: '22px 16px',
      }}>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 2 }}>{activePeriod?.name || 'Period'}</div>
        <div style={{ fontSize: 52, fontWeight: 900, lineHeight: 1, color: rankTint || 'var(--text-primary)' }}>
          {summary.shared_rank ? '=' : ''}{summary.rank}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>rank</div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 10 }}>
          <span style={{ fontSize: 22, fontWeight: 700 }}>{summary.total_points} pts</span>
          {myLeaderboardRow?.rank_delta ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 13, fontWeight: 700, color: myLeaderboardRow.rank_delta > 0 ? '#10b981' : '#ef4444' }}>
              {myLeaderboardRow.rank_delta > 0 ? <ArrowUp size={14} /> : <ArrowDown size={14} />}{Math.abs(myLeaderboardRow.rank_delta)}
            </span>
          ) : null}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginTop: 10 }}>
          {daysRemaining != null ? `${daysRemaining} days remaining` : 'Season in progress'}
        </div>
      </div>

      {/* 2. Gap card */}
      <div style={CARD}>
        <div style={CARD_TITLE}>Gap to the top</div>
        {summary.rank === 1 ? (
          <div style={{ fontSize: 14, fontWeight: 700, color: '#10b981' }}>You're in 1st place 🎉</div>
        ) : (
          <div style={{ fontSize: 14, marginBottom: summary.player_above ? 8 : 0 }}>
            To 1st place: <b>{summary.points_to_leader} pts</b>
          </div>
        )}
        {summary.player_above && (
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            To {(summary.rank - 1)}{summary.rank - 1 === 1 ? 'st' : summary.rank - 1 === 2 ? 'nd' : summary.rank - 1 === 3 ? 'rd' : 'th'} place ({summary.player_above.name}): <b>{summary.player_above.gap} pts</b>
          </div>
        )}
        {summary.player_below && (
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--border)' }}>
            {summary.player_below.name} is chasing from {summary.rank + 1}{summary.rank + 1 === 1 ? 'st' : summary.rank + 1 === 2 ? 'nd' : summary.rank + 1 === 3 ? 'rd' : 'th'} — <b>{summary.player_below.gap} pts</b> behind you
          </div>
        )}
      </div>

      {/* 3. Duel */}
      <div style={CARD}>
        <div style={CARD_TITLE}>Duel</div>
        {rival ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', marginBottom: 10 }}>
              <div style={{ textAlign: 'center' }}>
                <Avatar name={playerRecord?.name} photo={playerRecord?.photo_url} size={52} />
                <div style={{ fontSize: 12, fontWeight: 700, marginTop: 6 }}>You</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{myLeaderboardRow?.total_points ?? '—'} pts</div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-dimmed)' }}>VS</div>
              <div style={{ textAlign: 'center' }}>
                <Avatar name={rival.name} photo={rival.photo} size={52} />
                <div style={{ fontSize: 12, fontWeight: 700, marginTop: 6 }}>{rival.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{rival.total_points} pts</div>
              </div>
            </div>
            {headToHead && (
              <div style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 10 }}>
                {headToHead.a_wins}W · {headToHead.draws}D · {headToHead.a_losses}L over {headToHead.sessions.length} shared session{headToHead.sessions.length === 1 ? '' : 's'}
                {' · '}gap <b>{Math.abs((myLeaderboardRow?.total_points ?? 0) - rival.total_points)} pts</b>
              </div>
            )}
            <select
              value={duelRivalId || ''}
              onChange={e => setDuelRivalId(Number(e.target.value))}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit' }}
            >
              {leaderboard.filter(p => p.player_id !== playerId).map(p => (
                <option key={p.player_id} value={p.player_id}>{p.name} ({p.total_points} pts)</option>
              ))}
            </select>
          </>
        ) : (
          <div style={{ fontSize: 13, color: 'var(--text-dimmed)' }}>No rival yet — play a session to get started.</div>
        )}
      </div>

      {/* 4. Form */}
      <div style={CARD}>
        <div style={CARD_TITLE}>Form</div>
        <FormPills form={summary.form} />
        <div style={{ display: 'flex', gap: 16, marginTop: 12, fontSize: 12, color: 'var(--text-secondary)' }}>
          <span>Unbeaten run: <b>{summary.current_unbeaten_run}</b></span>
          <span>Longest: <b>{summary.longest_unbeaten_run}</b></span>
        </div>
        {sparklineData.length > 1 && (
          <div style={{ height: 50, marginTop: 12 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={sparklineData}>
                <Line type="monotone" dataKey="cumulative" stroke="#3b82f6" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
        {bestSession && (
          <div style={{ marginTop: 10, fontSize: 11, color: '#f59e0b' }}>
            🌟 Personal best: {fmtDate(bestSession.date)} — {(bestSession.base_points || 0) + (bestSession.bonus_points || 0)} pts
          </div>
        )}
      </div>

      {/* 5. My sessions */}
      <div style={CARD}>
        <div style={CARD_TITLE}>My sessions</div>
        {!summary.session_history?.length ? (
          <div style={{ fontSize: 13, color: 'var(--text-dimmed)' }}>No sessions played yet.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {summary.session_history.map((s, i) => {
              const meta = ssgResultMeta(s.base_points);
              const isBest = bestSession && bestSession.date === s.date;
              return (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8,
                  background: isBest ? '#f59e0b1a' : 'var(--bg-app)', border: isBest ? '1px solid #f59e0b55' : '1px solid var(--border)',
                }}>
                  <span style={{
                    width: 30, height: 30, borderRadius: 6, fontSize: 11, fontWeight: 700, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', background: meta.color + '33', color: meta.color,
                  }}>{meta.short}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600 }}>{fmtDate(s.date)}{s.is_mvp && <Star size={11} fill="#f59e0b" color="#f59e0b" style={{ verticalAlign: -1, marginLeft: 5 }} />}</div>
                    {s.bonus_points > 0 && (
                      <div style={{ fontSize: 11, color: '#f59e0b' }}>+{s.bonus_points} bonus{s.bonus_reason ? ` — ${s.bonus_reason}` : ''}</div>
                    )}
                    {s.note && <div style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>{s.note}</div>}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{(s.base_points || 0) + (s.bonus_points || 0)}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 6. Badges */}
      <div style={CARD}>
        <div style={CARD_TITLE}>Badges</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {SSG_BADGES.map(b => {
            const earned = earnedBadgeKeys.has(b.key);
            return (
              <div key={b.key} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '12px 8px',
                borderRadius: 10, background: 'var(--bg-app)', border: '1px solid var(--border)', opacity: earned ? 1 : 0.45,
              }}>
                <span style={{ fontSize: 24, filter: earned ? 'none' : 'grayscale(1)' }}>{b.emoji}</span>
                <div style={{ fontSize: 11.5, fontWeight: 700, marginTop: 6 }}>{b.label}</div>
                {!earned && <div style={{ fontSize: 9.5, color: 'var(--text-dimmed)', marginTop: 3 }}>{b.criterion}</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* 7. Full standings */}
      <div style={{ ...CARD, padding: 0, overflow: 'hidden' }}>
        <div style={{ ...CARD_TITLE, padding: '14px 16px 0' }}>Full standings</div>
        {myLeaderboardRow && (
          <div style={{
            position: 'sticky', top: 0, zIndex: 2, display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 16px', background: '#3b82f622', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', fontSize: 12.5,
          }}>
            <span style={{ width: 24, fontWeight: 700 }}>{myLeaderboardRow.shared_rank ? '=' : ''}{myLeaderboardRow.rank}</span>
            <span style={{ flex: 1, fontWeight: 700 }}>You</span>
            <span>{myLeaderboardRow.played}P</span>
            <span style={{ fontWeight: 700 }}>{myLeaderboardRow.total_points} pts</span>
          </div>
        )}
        <div style={{ maxHeight: 320, overflowY: 'auto' }}>
          {leaderboard.map(p => (
            <div key={p.player_id} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', fontSize: 12.5,
              background: p.player_id === playerId ? '#3b82f61a' : 'transparent', borderBottom: '1px solid var(--border)',
            }}>
              <span style={{ width: 24, fontWeight: 700, color: 'var(--text-secondary)' }}>{p.shared_rank ? '=' : ''}{p.rank}</span>
              <span style={{ flex: 1, fontWeight: p.player_id === playerId ? 700 : 400 }}>{p.name}</span>
              <span style={{ color: 'var(--text-muted)' }}>{p.played}P</span>
              <span style={{ fontWeight: 700, width: 40, textAlign: 'right' }}>{p.total_points}</span>
            </div>
          ))}
        </div>
      </div>

      {/* 8. Hall of Fame */}
      <div style={CARD}>
        <div style={CARD_TITLE}>Hall of Fame</div>
        {!hallOfFame || !hallOfFame.winners?.length ? (
          <div style={{ fontSize: 13, color: 'var(--text-dimmed)' }}>No period has been closed yet.</div>
        ) : (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6 }}>Period champions</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
              {hallOfFame.winners.map((w, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                  <Avatar name={w.name} photo={w.photo} size={28} />
                  <span style={{ flex: 1 }}>{w.name}</span>
                  <span style={{ color: 'var(--text-muted)' }}>{w.period_name}</span>
                </div>
              ))}
            </div>
          </>
        )}
        {hallOfFame?.summary?.length > 0 && (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6 }}>All-time top 5</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {hallOfFame.summary.slice(0, 5).map((s, i) => (
                <div key={s.player_id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                  <span style={{ width: 16, color: 'var(--text-dimmed)' }}>{i + 1}</span>
                  <Avatar name={s.name} photo={s.photo} size={28} />
                  <span style={{ flex: 1 }}>{s.name}</span>
                  <span style={{ fontWeight: 700 }}>{s.all_time_points} pts</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
